import { useCallback, useRef, useState } from 'react';
import {
  isAmbiguousTransportFailure,
  runCaptureWrite,
  type CaptureWriteOptions,
  type CaptureWriteOutcome,
} from '@/utils/mutation-retry';
import { toolErrorText } from '@/utils/tool-error';

export interface CaptureWriteRun<T> extends Omit<CaptureWriteOptions<T>, 'priorAttemptWasAmbiguous'> {
  onWritten: (outcome: CaptureWriteOutcome<T>) => void;
}

export interface CaptureWriteState {
  pending: boolean;
  error: string | null;
  run: <T>(options: CaptureWriteRun<T>) => Promise<void>;
  retry: () => void;
  clear: () => void;
}

// One sequence per surface: the member's Retry replays the identical options with the ambiguity
// flag set, so a retry after a possible partial write runs the duplicate precheck first.
export function useCaptureWrite(): CaptureWriteState {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const replay = useRef<(() => void) | null>(null);

  const execute = useCallback(
    async <T,>(options: CaptureWriteRun<T>, priorAttemptWasAmbiguous: boolean): Promise<void> => {
      setPending(true);
      setError(null);
      try {
        const outcome = await runCaptureWrite({ ...options, priorAttemptWasAmbiguous });
        replay.current = null;
        options.onWritten(outcome);
      } catch (caught) {
        const ambiguous = isAmbiguousTransportFailure(caught);
        replay.current = () => void execute(options, ambiguous);
        setError(toolErrorText(caught));
      } finally {
        setPending(false);
      }
    },
    []
  );

  const run = useCallback(
    <T,>(options: CaptureWriteRun<T>): Promise<void> => execute(options, false),
    [execute]
  );

  const retry = useCallback(() => {
    replay.current?.();
  }, []);

  const clear = useCallback(() => {
    replay.current = null;
    setError(null);
  }, []);

  return { pending, error, run, retry, clear };
}
